/**
 * Selection logic for the first-run setup wizard (`SetupWizard`).
 *
 * WHY THIS EXISTS: the original surface rendered all 11 registry steps in one
 * flat list — including the ~5 that report "Disponível após o passo anterior"
 * — so most of what a brand-new user saw was inert, and the one row they could
 * act on had no visual claim on their attention. The operator's report was
 * literally "I don't even know what to do in this screenshot".
 *
 * The fix is selection, not styling: show ONE actionable step, count only the
 * steps that gate the minimum operating profile, and move everything else out
 * of the first-run path.
 *
 * Pure + dependency-free so it is unit-testable under vitest's node env (the
 * SPA's UI behaviour is verified in-browser, never in jsdom — see the lane's
 * testing rules).
 */
import { SETUP_ITEM_ORDER } from "@/lib/contacts-logic";
import type { SetupStepId, SetupStepItem, SetupTier } from "@/types/contacts";

/** The tiers that make up the "minimum operating profile" — the same pair
 *  `SetupStatus.complete` is computed from. Everything else is deliberately
 *  NOT part of first-run: it belongs under "Depois".
 *
 *  `recommended` is excluded ON PURPOSE. It reads as important and would
 *  re-inflate the rail back toward the wall of rows this replaces. */
export const ESSENTIAL_TIERS: ReadonlySet<SetupTier> = new Set<SetupTier>([
  "required-blocking",
  "required-soft",
]);

/** Registry order, resolved against the live rows. The registry — not the
 *  server's array order — defines presentation order, matching what the
 *  previous checklist did so a returning user sees no reshuffle. */
function inRegistryOrder(items: SetupStepItem[]): SetupStepItem[] {
  const byId = new Map<SetupStepId, SetupStepItem>(items.map((i) => [i.id, i]));
  return SETUP_ITEM_ORDER.map((id) => byId.get(id)).filter(
    (i): i is SetupStepItem => i !== undefined,
  );
}

export function selectEssential(items: SetupStepItem[]): SetupStepItem[] {
  return inRegistryOrder(items).filter((i) => ESSENTIAL_TIERS.has(i.tier));
}

/**
 * The single step the wizard puts in front of the user: the first essential
 * step that is actually ACTIONABLE.
 *
 * `blocked` steps are skipped rather than shown-and-disabled — a row the user
 * cannot act on is exactly the noise this replaces. `skipped`/`deferred`/
 * `disabled` are also passed over: the user has already answered them, and
 * re-presenting an answered step reads as the app not listening.
 *
 * Returns `null` when nothing essential is actionable — either setup is done,
 * or everything left is blocked (a real state: the caller must say so rather
 * than render an empty card).
 */
export function selectActiveStep(items: SetupStepItem[]): SetupStepItem | null {
  return selectEssential(items).find((i) => !i.done && i.state === "pending") ?? null;
}

/** Progress over the ESSENTIAL set only, so "2 de 4" describes the finish
 *  line the user is actually running at — not 11 rows, most optional. */
export function essentialProgress(items: SetupStepItem[]): {
  done: number;
  total: number;
} {
  const essential = selectEssential(items);
  return {
    done: essential.filter((i) => i.done).length,
    total: essential.length,
  };
}

/** Everything that is NOT first-run essential and still open — the "Depois"
 *  drawer. Done items drop out: a collapsed list of things you already did is
 *  filler, and the wizard is not a receipt. */
export function selectLaterItems(items: SetupStepItem[]): SetupStepItem[] {
  return inRegistryOrder(items).filter((i) => !ESSENTIAL_TIERS.has(i.tier) && !i.done);
}

/**
 * Whether this step may be deferred from the wizard.
 *
 * `required-blocking` may NOT: the user-setup design ratified "keep it
 * foregrounded until the blocking set drains", and an escape hatch there
 * would break that. `required-soft` MAY — that is what keeps the surface from
 * being a wall the user cannot get past, which was the other half of the
 * operator's complaint.
 */
export function canDefer(item: SetupStepItem): boolean {
  return item.tier !== "required-blocking";
}

/** True when essential steps remain — i.e. render the WIZARD. When false the
 *  caller falls back to the flat review list, which is the right shape once
 *  there is nothing left to walk. */
export function hasOpenEssentials(items: SetupStepItem[]): boolean {
  return selectEssential(items).some((i) => !i.done);
}

/**
 * Whether the app is currently in the first-run setup flow.
 *
 * Keyed on the `?onboarding=1` param that `SetupGate` itself sets when it
 * routes a gated user to the setup surface — reusing the gate's own signal
 * rather than inventing a second notion of "is onboarding". Deliberately NOT
 * "any /identity visit": a fully-configured user editing their name later is
 * not onboarding, and suppressing unrelated UI for them would be scope creep.
 *
 * Used to keep the Agents sidebar from auto-expanding over a user who has no
 * context for it yet.
 */
export function isOnboardingActive(
  search: string = typeof window === "undefined" ? "" : window.location.search,
): boolean {
  return new URLSearchParams(search).get("onboarding") === "1";
}

/**
 * Carry the `?onboarding=1` signal into a step's DIALOG route.
 *
 * A flow-type step navigates away to open a connect dialog. Without the flag
 * the identity page stops being wizard-owned the moment the user arrives, so
 * the collapsed summary springs back open behind the dialog and the lawyer is
 * looking at two live copies of the same form again — the exact confusion the
 * wizard exists to remove.
 *
 * Narrow ON PURPOSE: only routes that open a dialog (`connect=`) get the flag.
 * The broader "always preserve it" rule was considered and REJECTED — the
 * "Depois" items point at the very cards the wizard HIDES while it owns input
 * (`/identity?card=assistant`), so preserving the flag there would navigate
 * the lawyer to something the page then hides from them. Recorded so the next
 * session does not re-open a settled call.
 */
export function withOnboardingSignal(
  route: string,
  onboarding: boolean = isOnboardingActive(),
): string {
  if (!onboarding) return route;
  const [path, query = ""] = route.split("?");
  const params = new URLSearchParams(query);
  if (!params.has("connect")) return route;
  params.set("onboarding", "1");
  return `${path}?${params.toString()}`;
}
